import { API_URL } from "config";
import {
  createApi,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import type {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
} from "@reduxjs/toolkit/query";
import { RootState, AppDispatch } from "./store";
import { logout } from "features/auth/authSlice";
import {
  ensureFreshAccessToken,
  isAccessTokenFresh,
} from "features/auth/tokenRefresh";
import {
  pushNotification,
  showSessionExpired,
} from "features/notificationSlice";

// Mutations allowed while Client View is active.
const CLIENT_VIEW_ALLOWED_MUTATIONS = new Set([
  "login",
  "logout",
  "checkAuth",
  // Reads the API models as mutations: they only issue download links.
  "getSecureFileAccessUrl",
  "getSecureFilesAccessUrls",
]);

// Never carry X-View-As-User: these authenticate the admin, not the viewed user.
const SESSION_ENDPOINTS = new Set([
  "login",
  "logout",
  "checkAuth",
]);

const CLIENT_VIEW_READ_ONLY_MESSAGE =
  "Client View is read-only. Exit Client View to make changes.";

function isAuthUrl(args: string | FetchArgs): boolean {
  const url = typeof args === "string" ? args : args.url;
  return url.startsWith("/auth/");
}

const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState, endpoint }) => {
    const state = getState() as RootState;
    const token = state.auth.accessToken;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    const { clientView } = state;
    if (
      clientView?.active &&
      clientView.userId != null &&
      !SESSION_ENDPOINTS.has(endpoint)
    ) {
      headers.set("X-View-As-User", String(clientView.userId));
    }
    return headers;
  },
});

/**
 * End the session once, for a refusal we know is final.
 *
 * Only "dead" gets here. A network blip or a 5xx leaves the session alone -
 * treating those as a dead session was half of why an open tab kept landing
 * on the login page (CAL-5974).
 */
function endSession(api: BaseQueryApi): void {
  api.dispatch(showSessionExpired());
  api.dispatch(logout());
}

// A refusal can mean the user's access changed since /me was loaded; reload it
// so menus and buttons catch up, at most once a minute.
const PROFILE_REFRESH_MS = 60_000;
let lastProfileRefresh = 0;

function refreshProfileAfterRefusal(
  api: BaseQueryApi,
  args: string | FetchArgs,
): void {
  const url = typeof args === "string" ? args : args.url;
  const now = Date.now();
  if (
    url.startsWith("/users/me/") ||
    now - lastProfileRefresh < PROFILE_REFRESH_MS
  )
    return;
  lastProfileRefresh = now;
  if (invalidateProfile) api.dispatch(invalidateProfile());
}

// Set once the API exists; the base query is defined before it.
let invalidateProfile:
  | (() => ReturnType<typeof api.util.invalidateTags>)
  | null = null;

// Base query signature shared with helpers that wrap the queryFn base query
// (features/integrationsBeta/paginatedList.ts). Meta is RTK's default `{}`.
export type AppBaseQueryFn = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
>;

const baseQueryWithReauth: AppBaseQueryFn = async (args, api, extraOptions) => {
  const state = api.getState() as RootState;
  const accessToken = state.auth.accessToken;

  if (
    api.type === "mutation" &&
    state.clientView?.active &&
    !CLIENT_VIEW_ALLOWED_MUTATIONS.has(api.endpoint) &&
    !isAuthUrl(args)
  ) {
    api.dispatch(
      pushNotification({
        id: "client-view-read-only",
        message: CLIENT_VIEW_READ_ONLY_MESSAGE,
        severity: "warning",
        autoHideMs: 5000,
      }),
    );
    return {
      error: {
        status: 403,
        data: { error: CLIENT_VIEW_READ_ONLY_MESSAGE },
      } as unknown as FetchBaseQueryError,
    };
  }

  const dispatch = api.dispatch as AppDispatch;

  // Refresh before spending a token that is about to expire.
  if (accessToken && !isAccessTokenFresh(accessToken)) {
    const outcome = await ensureFreshAccessToken(dispatch);
    if (outcome === "dead") {
      endSession(api);
      return {
        error: { status: 401, data: "Session expired" } as FetchBaseQueryError,
      };
    }
    // "transient" falls through: the request goes out on the current token and
    // fails on its own terms rather than taking the session down with it.
  }

  let result = await baseQuery(args, api, extraOptions);
  if (result.error?.status === 403) refreshProfileAfterRefusal(api, args);
  if (!result.error || result.error.status !== 401) return result;

  const outcome = await ensureFreshAccessToken(dispatch);
  if (outcome === "dead") {
    endSession(api);
    return result;
  }
  if (outcome === "transient") return result;

  // checkAuth carries the token in its body, so the retry has to be re-armed
  // with the new one - the stale copy would just 401 again.
  if (typeof args !== "string" && args.url === "/auth/token/verify/") {
    const token = (api.getState() as RootState).auth.accessToken;
    if (token) args.body = { token };
  }

  result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    // A token minted seconds ago and still refused: the session really is gone.
    endSession(api);
  }
  return result;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Auth",
    "FileSystem",
    "AdminUsers",
    "AdminClients",
    "BotJobData",
    "BotNotes",
    "IntegrationSettings",
    "SftpConnections",
    "MajorJobs",
    "Partners",
    "IntegrationMonitor",
    "IntegrationRuns",
    "IntegrationConnection",
    "IntegrationCredential",
    "IntegrationSchedule",
    "IntegrationReadiness",
    "IntegrationProvider",
    "IntegrationConfigProfile",
    "BrowserProfile",
    "WebhookTrigger",
    "SecureUploads",
    "SecureUploadsUnread",
    "SecureProjects",
    "PdfSplitJobs",
    "AccessGroups",
  ],
  endpoints: () => ({}),
});
invalidateProfile = () => api.util.invalidateTags(["Auth"]);

export default api;
